/**
 * VH Staff Forgot Password Page
 *
 * Allows staff to request a password reset email.
 * URL format: /staff/:slug/forgot-password
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

interface Restaurant {
  restaurant_id: number;
  restaurant_name: string;
  slug: string;
  rest_account_id: number;
  logo_url: string | null;
}

const VHStaffForgotPassword = () => {
  const { slug } = useParams<{ slug: string }>();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [restaurantLoading, setRestaurantLoading] = useState(true);
  const [restaurantError, setRestaurantError] = useState('');

  // Fetch restaurant info by slug
  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!slug) {
        setRestaurantError('Invalid restaurant URL');
        setRestaurantLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/vh-staff/auth/restaurant/${slug}`);
        setRestaurant(response.data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setRestaurantError('Restaurant not found');
        } else {
          setRestaurantError('Failed to load restaurant info');
        }
      } finally {
        setRestaurantLoading(false);
      }
    };

    fetchRestaurant();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await apiClient.post('/vh-staff/auth/request-password-reset', { email });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please try again in an hour.');
      } else {
        // Even on errors, show success message to prevent email enumeration
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state for restaurant info
  if (restaurantLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#FDB913] animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Restaurant not found
  if (restaurantError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Restaurant Not Found</h1>
          <p className="text-gray-500 mb-6">{restaurantError}</p>
          <Link
            to="/vh-staff-login"
            className="block w-full py-3 bg-[#FDB913] text-gray-900 rounded-lg font-semibold hover:bg-[#e5a811] transition-colors"
          >
            Go to Staff Login
          </Link>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
          <p className="text-gray-500 mb-6">
            If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
          </p>
          <p className="text-sm text-gray-400 mb-6">
            The link will expire in 15 minutes.
          </p>
          <Link
            to={`/staff/${slug}`}
            className="block w-full py-3 bg-[#FDB913] text-gray-900 rounded-lg font-semibold hover:bg-[#e5a811] transition-colors"
          >
            Back to Login
          </Link>
        </motion.div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full"
      >
        {/* Header with Restaurant Branding */}
        <div className="text-center mb-8">
          {restaurant?.logo_url ? (
            <img
              src={restaurant.logo_url}
              alt={restaurant.restaurant_name}
              className="h-20 w-auto max-w-[280px] object-contain mx-auto mb-4"
            />
          ) : (
            <div className="h-16 w-16 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-[#FDB913] font-bold text-xl">AI</span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            Forgot Password?
          </h1>
          <p className="text-gray-500 mt-2">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full py-3 bg-[#FDB913] text-gray-900 rounded-lg font-semibold hover:bg-[#e5a811] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <div className="h-5 w-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <Link
            to={`/staff/${slug}`}
            className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
        </div>

        {/* Powered by Dine AI */}
        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">
            Powered by{' '}
            <span className="font-medium">
              Dine <span className="text-[#FDB913]">AI</span>
            </span>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VHStaffForgotPassword;
